import { Dispatch, SetStateAction, useContext, useEffect, useState } from 'react';
import {
	MinimalGeocodeResult,
	extractAddressFromComponents,
	fullAddressProps,
} from '@/app/(ordering)/shop/menu/ShopTabNavigation/Delivery/DeliverySearch';

import { BackArrow } from '@/components/common/Icons/BackArrow';
import { Button } from '@/components/common/Button/Button';
import { CartContext } from '@/app/(ordering)/shop/CartProvider/CartProvider';
import { DeliveryCurrentView } from '@/app/(ordering)/shop/menu/ShopTabNavigation/Delivery/DeliveryPanel';
import { ErrorObj } from '@/utils/parseError';
import { InputGroup } from '@/components/common/InputGroup/InputGroup';
import { TextArea } from '@/components/common/TextArea/TextArea';
import { deliveryAddressProps } from '@/app/(ordering)/shop/menu/data/shopdata.type';
import type { signinResponse } from '@/utils/authClient.types';
import styles from '@/app/(ordering)/shop/shop.module.css';

interface DeliveryAddressProps {
	setCurrentView: (view: DeliveryCurrentView) => void;
	selectedAddress: MinimalGeocodeResult[] | undefined;
	setDeliveryAddress: (newAddress: deliveryAddressProps | undefined) => void;
	onBack: () => void;
	setIsAddressAvailable: Dispatch<SetStateAction<boolean>>;
	userDetails?: signinResponse | undefined;
}
export function DeliveryAddressForm({
	setCurrentView,
	selectedAddress,
	setDeliveryAddress,
	setIsAddressAvailable,
	onBack,
}: DeliveryAddressProps) {
	const { isUndeliverable, setIsUndeliverable, createTempBasket } = useContext(CartContext);

	const [addressFields, setAddressFields] = useState<{
		streetAddress: string;
		building: string;
		city: string;
		state: string;
		zipCode: string;
		specialInstructions: string;
	}>({
		streetAddress: '',
		building: '',
		city: '',
		state: '',
		zipCode: '',
		specialInstructions: '',
	});

	const [isLoading, setIsLoading] = useState(false);
	const [errMsg, setErrMsg] = useState(
		'Delivery is not available to that address. Please update your address or change to Pickup.'
	);
	useEffect(() => {
		const fullAddress: Partial<fullAddressProps> | null = selectedAddress
			? extractAddressFromComponents(selectedAddress[0].address_components)
			: null;

		const streetAddress = `${fullAddress?.street_number ?? ''} ${fullAddress?.route ?? ''}`;
		const city = fullAddress?.locality ?? '';
		const state = fullAddress?.administrative_area_level_1 ?? '';
		const zipCode = fullAddress?.postal_code ?? '';

		const initialState = {
			streetAddress,
			building: '',
			city,
			state,
			zipCode,
			specialInstructions: '',
		};

		setAddressFields(initialState);
	}, [selectedAddress]);

	const handleChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
		const { target } = event;
		setAddressFields((prevState) => ({
			...prevState,
			[target.name]: target.value,
		}));
	};

	const handleSubmit = async (event: React.SyntheticEvent<HTMLFormElement>) => {
		event.preventDefault();
		try {
			setIsUndeliverable(false);
			// For specialInstructions Regex used to filter out non alphanumeric/punctuation characters upon submission
			if (addressFields.specialInstructions) {
				const filteredString = addressFields.specialInstructions
					.replace(/[^a-zA-Z0-9 ]/g, '')
					.replace(/  +/g, ' ');
				addressFields.specialInstructions = filteredString;
			}

			// Track the CONFIRM ADDRESS button click
			if (typeof window !== 'undefined') {
				window.dataLayer = window.dataLayer || [];
				const eventData = {
					event: 'journey_element',
					eventModel: {
						type: 'button',
						action: 'click',
						location: 'delivery address form',
						text: 'CONFIRM ADDRESS',
						pathname: window.location.pathname,
						domain: window.location.hostname,
					},
				};
				window.dataLayer.push(eventData);
			}
			setIsLoading(true);

			// creating a temporary basket to get wanted times
			await createTempBasket({
				type: 'delivery',
				deliveryAddress: {
					streetAddress: addressFields.streetAddress,
					city: addressFields.city,
					state: addressFields.state,
					zipCode: addressFields.zipCode,
					latitude: (selectedAddress && selectedAddress[0].geometry.location.lat()) ?? 0,
					longitude: (selectedAddress && selectedAddress[0].geometry.location.lng()) ?? 0,
					specialInstructions: '',
				},
				pickupAddress: null,
				orderDateTime: {
					orderDate: new Date(),
					orderTime: 'asap',
				},
			});

			setIsAddressAvailable(true);
			setDeliveryAddress({
				id: 0,
				lastPickupStoreNumber: 10000,
				lastDeliveryAddress: {
					streetAddress: addressFields.streetAddress,
					building: addressFields.building,
					city: addressFields.city,
					state: addressFields.state,
					zipCode: addressFields.zipCode,
					specialInstructions: addressFields.specialInstructions,
				},
				type: 'delivery',
				latitude: selectedAddress && selectedAddress[0].geometry.location.lat(),
				longitude: selectedAddress && selectedAddress[0].geometry.location.lng(),
				storenumber: 10000,
			});
			setIsLoading(false);
			setCurrentView('selection');
		} catch (error) {
			setIsLoading(false);
			setIsUndeliverable(true);
			setErrMsg((error as ErrorObj).message);
		}
	};

	const dontUseAddressClickToAction = () => {
		setIsUndeliverable(false);
		setCurrentView('searching');
	};

	return (
		<div className={styles.shopSearch}>
			<div className="fixed">
				<button
					onClick={() => {
						setIsUndeliverable(false);
						onBack();
					}}
				>
					<BackArrow aria-label="back" className={styles.backArrow} />
				</button>
			</div>
			<h3 className={`${styles.title} order-title text-center`} data-type-style="subtitle-2">
				Delivery Location
			</h3>
			<div className="mb-4 flex flex-row">
				<h2 data-type-style="title-4">delivery address</h2>
			</div>
			{isUndeliverable && (
				<p className="mb-4 text-red-600" data-type-style="metadata-1">
					{errMsg}
				</p>
			)}
			<form
				onSubmit={(event) => {
					handleSubmit(event);
				}}
			>
				<div className="mb-6">
					<InputGroup
						disabled={true}
						name="streetAddress"
						value={addressFields?.streetAddress || ''}
						onChange={handleChange}
						label="ADDRESS"
						className="mt-2 block w-full rounded-lg border border-gray-500 bg-gray-200 p-2.5 text-base text-gray-800 focus:outline-green-600 focus:ring-green-500"
					/>
				</div>
				<div className="mb-6">
					<InputGroup
						name="building"
						value={addressFields?.building || ''}
						onChange={handleChange}
						label="APT / SUITE (OPTIONAL)"
						maxLength={50}
						className="mt-2 block w-full rounded-lg border border-gray-500 p-2.5 text-base text-gray-800 focus:outline-green-600 focus:ring-green-500"
					/>
				</div>
				<div className="mb-6">
					<InputGroup
						disabled={true}
						name="city"
						value={addressFields?.city || ''}
						onChange={handleChange}
						label="CITY"
						className="mt-2 block w-full rounded-lg border border-gray-500 bg-gray-200 p-2.5 text-base text-gray-800 focus:outline-green-600 focus:ring-green-500"
					/>
				</div>
				<div className="mb-6 grid gap-6 md:grid-cols-2">
					<div>
						<InputGroup
							disabled={true}
							name="state"
							value={addressFields?.state || ''}
							onChange={handleChange}
							label="STATE"
							className="mt-2 block w-full rounded-lg border border-gray-500 bg-gray-200 p-2.5 text-base text-gray-800 focus:outline-green-600 focus:ring-green-500"
						/>
					</div>
					<div>
						<InputGroup
							name="zipCode"
							disabled={true}
							value={addressFields?.zipCode || ''}
							onChange={handleChange}
							label="ZIP CODE"
							className="mt-2 block w-full rounded-lg border border-gray-500 bg-gray-200 p-2.5 text-base text-gray-800 focus:outline-green-600 focus:ring-green-500"
						/>
					</div>
				</div>
				<div className="mb-6">
					<TextArea
						style={{
							border: '1px solid var(--grey-500, #989B9C)',
							padding: '15px',
						}}
						name="specialInstructions"
						value={addressFields?.specialInstructions || ''}
						onChange={handleChange}
						label="DELIVERY INSTRUCTIONS"
						placeholder="e.g. ring doorbell upon drop off"
						maxLength={128}
					/>
				</div>
				<div className="mx-auto mt-auto grid w-full max-w-xs grid-cols-1 gap-6">
					<Button
						variant="primary"
						size="large"
						data-type-style="button-mobile"
						isLoading={isLoading}
					>
						CONFIRM ADDRESS
					</Button>
				</div>
			</form>
			<div className="mt-6 pb-10 text-center">
				<Button
					className="font-black"
					variant="text"
					data-type-style="button-web"
					onClick={() => dontUseAddressClickToAction()}
				>
					DON&apos;T USE THIS ADDRESS
				</Button>
			</div>
		</div>
	);
}
